import { useCallback, useMemo } from 'react';
import type {
  CustomChartDefinition,
  CustomChartTableSettings,
  DashboardPreset,
  DashboardPresetContent,
  DashboardPresetFilters,
} from '@/entities/dashboards/model/api/types';
import { useHasPermission } from '@/entities/profile';
import {
  toSharedPresetListFields,
  useSharedPresets,
  useSharePreset,
  type SharedPreset,
  type SharedPresetTransfer,
  type SharedPresetUser,
} from '@/entities/shared-presets';
import { useCreateSystemPreset, useSystemPresets } from '@/entities/system-presets';
import { isQueryDataReceived } from '@/shared/api';
import {
  buildSharedDashboardPresetId,
  buildSystemDashboardPresetId,
  DASHBOARD_PRESET_KEY_PARSERS,
  DASHBOARD_PRESETS_TABLE_NAME,
  parseSharedDashboardPresetId,
} from './dashboardPresetKeys';
import {
  type DashboardPresetChanges,
  readServerFilters,
  useServerDashboardPresetWrite,
} from './useServerDashboardPresetWrite';
import { useServerPresetMutations } from './useServerPresetMutations';
import { useSettings } from './useSettings';

/** Дашборд в списке страницы дашбордов: личный, совместный либо системный, с правами на него. */
export type DashboardPresetItem = DashboardPreset & {
  /** Дашборд системный — общий для всех пользователей */
  isSystem: boolean;
  /** Дашборд совместный — им владелец поделился с выбранными пользователями */
  isShared: boolean;
  /** Владелец совместного дашборда */
  owner?: SharedPresetUser;
  /** Получатели совместного дашборда — известны только его владельцу */
  recipients?: SharedPresetUser[];
  /**
   * Текущий пользователь вправе править дашборд: свой личный, совместный, которым владеет, либо
   * системный, который он сам сделал системным
   */
  isEditable: boolean;
};

const byName = (a: DashboardPresetItem, b: DashboardPresetItem): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Фильтры панели дашборда пользователь меняет у себя, а не в самом дашборде: дашборд серверный
 * (совместный или системный), а править его пользователь не вправе.
 */
const isOwnFiltersHolder = (preset: DashboardPresetItem): boolean =>
  (preset.isShared || preset.isSystem) && !preset.isEditable;

/**
 * Хук работы с пользовательскими дашбордами-пресетами страницы дашбордов.
 *
 * Пресет здесь — самостоятельный состав диаграмм, а не снимок текущего вида экрана: создание
 * заводит новый дашборд, а правка меняет его состав. Поэтому «сохранить текущее состояние в
 * пресет» — операции, обратной применению, — у дашбордов нет.
 *
 * Личные дашборды лежат в настройках пользователя, системные и совместные — на сервере и попадают в
 * тот же список: системные первыми, за ними совместные, затем личные. У совместного личные применение
 * и закрепление, а название, состав и фильтры панели общие для владельца и получателей. У системного
 * личное только применение: название, состав, фильтры и закрепление общие для всех, а ведёт их
 * любой обладатель разрешения на системные пресеты, кто бы ни сделал дашборд системным. Пока
 * системные и совместные не получены, отсутствие дашборда в списке ещё ничего не означает
 * (`isPresetListKnown`).
 *
 * Фильтры панели тот, кто серверный дашборд не правит (получатель совместного, пользователь без
 * разрешения у системного), вправе сменить у себя: смена запоминается за ним и действует, пока он её
 * не сбросит, — правка фильтров самого дашборда до него тем временем не доходит.
 */
export const useDashboardPresets = () => {
  const {
    userId,
    dashboardPresets,
    createDashboardPreset,
    updateDashboardPreset,
    applyDashboardPreset,
    clearActiveDashboardPreset,
    deleteDashboardPreset,
    setDashboardPresetPinned,
    setDashboardChartTableSettings,
    setDashboardSharedFilters,
    pruneDashboardSharedFilters,
  } = useSettings();

  const canManageSystemPresets = useHasPermission('ADMIN_SYSTEM_PRESET');
  const systemPresetsQuery = useSystemPresets<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);
  const systemPresetsData = systemPresetsQuery.data;
  const { mutateAsync: createSystemPreset, isPending: isMakingSystem } =
    useCreateSystemPreset<DashboardPresetContent>();
  const sharedPresetsQuery = useSharedPresets<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);
  const sharedPresetsData = sharedPresetsQuery.data;
  const { writeServerPreset, isUpdating } = useServerDashboardPresetWrite();
  const { pinServerPreset, deleteServerPreset } = useServerPresetMutations(DASHBOARD_PRESET_KEY_PARSERS);

  const systemPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = (systemPresetsData?.items ?? []).map<DashboardPresetItem>((preset) => ({
      id: buildSystemDashboardPresetId(preset.id),
      name: preset.name,
      pinned: preset.isPinned,
      charts: preset.settings.charts,
      filters: readServerFilters(preset.settings),
      isSystem: true,
      isShared: false,
      isEditable: canManageSystemPresets,
    }));
    list.sort(byName);
    return list;
  }, [systemPresetsData, canManageSystemPresets]);

  const sharedPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = (sharedPresetsData?.items ?? []).map<DashboardPresetItem>((preset) => ({
      id: buildSharedDashboardPresetId(preset.id),
      ...toSharedPresetListFields(preset),
      charts: preset.settings.charts,
      filters: readServerFilters(preset.settings),
      isSystem: false,
    }));
    list.sort(byName);
    return list;
  }, [sharedPresetsData]);

  const personalPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = Object.values(dashboardPresets.presets ?? {}).map<DashboardPresetItem>((preset) => ({
      ...preset,
      isSystem: false,
      isShared: false,
      isEditable: true,
    }));
    list.sort(byName);
    return list;
  }, [dashboardPresets.presets]);

  // Порядок тот же, что у пресетов таблицы: общий для всех набор, затем совместные, затем личные
  const presets = useMemo(
    () => [...systemPresets, ...sharedPresets, ...personalPresets],
    [systemPresets, sharedPresets, personalPresets],
  );

  // Отказ запроса серверных дашбордов — не пустой список: до ответа отсутствие дашборда ничего не означает
  const isPresetListKnown = isQueryDataReceived(systemPresetsQuery) && isQueryDataReceived(sharedPresetsQuery);

  const activePresetId = dashboardPresets.activePresetId;
  const activePreset = useMemo(
    () => presets.find((preset) => preset.id === activePresetId) ?? null,
    [presets, activePresetId],
  );

  const sharedFilters = dashboardPresets.sharedFilters;

  /**
   * Фильтры дашборда для текущего пользователя: у того, кто серверный дашборд не правит, — свои,
   * если он их менял.
   */
  const resolveFilters = useCallback(
    (preset: DashboardPresetItem): DashboardPresetFilters | null => {
      const ownFilters = isOwnFiltersHolder(preset) ? sharedFilters?.[preset.id] : undefined;
      return ownFilters ?? preset.filters ?? null;
    },
    [sharedFilters],
  );

  const activeFilters = useMemo(
    () => (activePreset ? resolveFilters(activePreset) : null),
    [activePreset, resolveFilters],
  );

  const createPreset = useCallback(
    (name: string, charts: CustomChartDefinition[], filters: DashboardPresetFilters): string =>
      createDashboardPreset(userId, name, charts, filters),
    [createDashboardPreset, userId],
  );

  /**
   * Записывает название, состав и/или фильтры. Системный и совместный дашборды записываются на
   * сервере, поэтому итог приходит обещанием: `false` — сервер правку не принял, о причине сообщил
   * общий перехватчик.
   */
  const updatePreset = useCallback(
    async (presetId: string, changes: DashboardPresetChanges): Promise<boolean> => {
      const isWritten = await writeServerPreset(presetId, changes, 'charts');
      if (isWritten !== null) return isWritten;
      updateDashboardPreset(userId, presetId, changes);
      return true;
    },
    [updateDashboardPreset, writeServerPreset, userId],
  );

  /**
   * Запоминает смену фильтров применённого дашборда там, откуда они взяты: у личного — в нём самом,
   * у серверного, который пользователь правит, — на сервере, у прочих — за ним самим. `false` —
   * сервер смену не принял, о причине сообщил общий перехватчик.
   */
  const setActiveFilters = useCallback(
    async (filters: DashboardPresetFilters): Promise<boolean> => {
      if (!activePreset) return false;
      if (isOwnFiltersHolder(activePreset)) {
        setDashboardSharedFilters(userId, activePreset.id, filters);
        return true;
      }
      const isWritten = await writeServerPreset(activePreset.id, { filters }, 'filters');
      if (isWritten !== null) return isWritten;
      updateDashboardPreset(userId, activePreset.id, { filters });
      return true;
    },
    [activePreset, updateDashboardPreset, setDashboardSharedFilters, writeServerPreset, userId],
  );

  /**
   * «Сбросить фильтры» на применённом дашборде. Тот, кто серверный дашборд не правит, возвращается к
   * его фильтрам — и дальше их правка снова доходит до него, — у остальных дашборд получает фильтры
   * по умолчанию.
   */
  const resetActiveFilters = useCallback(
    async (defaults: DashboardPresetFilters): Promise<boolean> => {
      if (activePreset && isOwnFiltersHolder(activePreset)) {
        setDashboardSharedFilters(userId, activePreset.id, null);
        return true;
      }
      return setActiveFilters(defaults);
    },
    [activePreset, setDashboardSharedFilters, setActiveFilters, userId],
  );

  /**
   * Забывает свои фильтры пользователя у серверных дашбордов, которых в списке нет. Зовётся только
   * с полученным списком (`isPresetListKnown`): до него пропавшим выглядит любой серверный.
   */
  const pruneSharedFilters = useCallback(
    () => pruneDashboardSharedFilters(userId, new Set([...systemPresets, ...sharedPresets].map((preset) => preset.id))),
    [pruneDashboardSharedFilters, systemPresets, sharedPresets, userId],
  );

  const apply = useCallback(
    (presetId: string) => applyDashboardPreset(userId, presetId),
    [applyDashboardPreset, userId],
  );

  const clearActive = useCallback(() => clearActiveDashboardPreset(userId), [clearActiveDashboardPreset, userId]);

  /**
   * Удаляет дашборд: системный — у всех, у получателя совместного — убирает только из его списка.
   * `onRemoved` — когда дашборд удалён: у серверного по ответу сервера, отказ его не вызывает.
   */
  const remove = useCallback(
    (presetId: string, onRemoved?: () => void) => {
      // Серверный пропадает из списка только с ответом сервера, поэтому и применение снимается
      // по ответу: отказ оставляет дашборд применённым
      const isServerPreset = deleteServerPreset(presetId, () => {
        if (activePresetId === presetId) clearActiveDashboardPreset(userId);
        onRemoved?.();
      });
      if (isServerPreset) return;
      deleteDashboardPreset(userId, presetId);
      onRemoved?.();
    },
    [deleteDashboardPreset, deleteServerPreset, clearActiveDashboardPreset, userId, activePresetId],
  );

  const setPinned = useCallback(
    (presetId: string, pinned: boolean) => {
      if (!pinServerPreset(presetId, pinned)) setDashboardPresetPinned(userId, presetId, pinned);
    },
    [setDashboardPresetPinned, pinServerPreset, userId],
  );

  const findPersonalTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<DashboardPresetContent> | undefined => {
      const preset = personalPresets.find((item) => item.id === presetId);
      return (
        preset && {
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
          pinned: preset.pinned,
        }
      );
    },
    [personalPresets],
  );

  const findSharedTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<DashboardPresetContent> | undefined => {
      const preset = sharedPresets.find((item) => item.id === presetId);
      return (
        preset && {
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
          pinned: preset.pinned,
        }
      );
    },
    [sharedPresets],
  );

  const replaceWithShared = useCallback(
    (personalId: string, created: SharedPreset<DashboardPresetContent>, wasActive: boolean) => {
      // Применённость переходит на совместный до удаления личного — иначе удаление сняло бы её
      if (wasActive) applyDashboardPreset(userId, buildSharedDashboardPresetId(created.id));
      deleteDashboardPreset(userId, personalId);
    },
    [applyDashboardPreset, deleteDashboardPreset, userId],
  );

  const replaceWithPersonal = useCallback(
    (_sharedPresetId: string, preset: SharedPresetTransfer<DashboardPresetContent>, wasActive: boolean) => {
      // Личный — свой состав, а не ссылка на состав из ответа сервера
      const { charts, filters } = preset.settings;
      createDashboardPreset(
        userId,
        preset.name,
        structuredClone(charts),
        filters ? structuredClone(filters) : undefined,
        {
          pinned: preset.pinned,
          apply: wasActive,
        },
      );
    },
    [createDashboardPreset, userId],
  );

  /**
   * Поделиться дашбордом: личный становится совместным с теми же названием, составом, фильтрами
   * и закреплением, у совместного меняются получатели, без получателей он возвращается в личные.
   */
  const { share, isSharing } = useSharePreset<DashboardPresetContent>({
    tableName: DASHBOARD_PRESETS_TABLE_NAME,
    activePresetId,
    parseSharedId: parseSharedDashboardPresetId,
    findPersonal: findPersonalTransfer,
    findShared: findSharedTransfer,
    replaceWithShared,
    replaceWithPersonal,
  });

  /**
   * Делает личный дашборд системным: системный получает его название, состав и фильтры, личный по
   * ответу сервера уходит из списка, а применённость переходит на системный. Закрепление не
   * переносится: у личного оно личное, у системного — общее для всех. Ответ сервера возвращается
   * вызывающему: пока он не пришёл, повторное нажатие завело бы второй системный дашборд.
   */
  const makeSystem = useCallback(
    async (presetId: string) => {
      const preset = personalPresets.find((item) => item.id === presetId);
      if (!preset) return;
      await createSystemPreset(
        {
          tableName: DASHBOARD_PRESETS_TABLE_NAME,
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
        },
        {
          onSuccess: (created) => {
            // Применённость переходит на системный до удаления личного — иначе удаление сняло бы её
            if (activePresetId === presetId) applyDashboardPreset(userId, buildSystemDashboardPresetId(created.id));
            deleteDashboardPreset(userId, presetId);
          },
        },
      );
    },
    [createSystemPreset, applyDashboardPreset, deleteDashboardPreset, personalPresets, activePresetId, userId],
  );

  /**
   * Заводит личную копию дашборда под переданным названием и применяет её: сам дашборд не меняется.
   * Копия получает фильтры, которые пользователь видит в панели.
   * @returns Идентификатор копии.
   */
  const saveCopy = useCallback(
    (presetId: string, name: string): string | null => {
      const preset = presets.find((item) => item.id === presetId);
      if (!preset) return null;
      const filters = resolveFilters(preset);
      // Копия — свой состав, а не ссылка на состав из ответа сервера
      return createDashboardPreset(
        userId,
        name,
        structuredClone(preset.charts),
        filters ? structuredClone(filters) : undefined,
      );
    },
    [createDashboardPreset, presets, resolveFilters, userId],
  );

  const chartTables = dashboardPresets.chartTables;

  const setChartTableSettings = useCallback(
    (chartId: string, settings: Partial<CustomChartTableSettings>) =>
      setDashboardChartTableSettings(userId, chartId, settings),
    [setDashboardChartTableSettings, userId],
  );

  return {
    presets,
    isPresetListKnown,
    /**
     * Системные или совместные дашборды ещё загружаются; после отказа запроса — уже нет, хотя список
     * и не получен
     */
    isPresetListLoading: systemPresetsQuery.isLoading || sharedPresetsQuery.isLoading,
    /**
     * Пользователь вправе делать личные дашборды системными, а также править, закреплять и удалять
     * системные (`isEditable`)
     */
    canManageSystemPresets,
    activePresetId,
    activePreset,
    /** Фильтры панели применённого дашборда; `null` — своих фильтров нет, действуют фильтры по умолчанию */
    activeFilters,
    /** Личный вид таблиц диаграмм по идентификатору диаграммы */
    chartTables,
    /** Запись состава серверного дашборда — системного или совместного — в работе */
    isUpdating,
    isSharing,
    createPreset,
    updatePreset,
    setActiveFilters,
    resetActiveFilters,
    pruneSharedFilters,
    apply,
    clearActive,
    remove,
    setPinned,
    share,
    makeSystem,
    /** Личный дашборд переводится в системные — до ответа сервера повторно не переводится */
    isMakingSystem,
    saveCopy,
    setChartTableSettings,
  };
};
